/**
 * Bereich *Kalender* (Spec 07, Spec 45) — erster Eintrag der Symbolleiste,
 * Route `/`. Vier Wochen ab Montag, alle vier Arten (Sollstellung, Frist,
 * Termin, Aufgabe) vom Server zusammengeführt, gebündelt und sortiert
 * (`GET /calendar`, Regel 2, I9). Seit ADR 0033/Spec 45 gehen die Aufgaben
 * hier auf: Sie stehen an ihrem Tag, ein Ring erledigt sie sofort (R16), ein
 * Klick auf den Titel öffnet `task-sheet`.
 *
 * **Zwei Ansichten, eine URL** (`/?view=`): `wochen` (Vorgabe, die vier
 * Wochen) und die Listen `ueberfaellig`/`meine` der Seitenleiste
 * (`router.tsx::KalenderSidebar`) — dieselbe `TaskList` wie am Objekt, mit
 * `GET /tickets?overdue=…&open=…` bzw. `?assignee=me&open=…`. Die Mengen
 * definiert der Server; hier steht nichts, was Aufgaben zählt oder filtert.
 *
 * **Kein Client rechnet einen Wochenanfang aus** (Spec, „Das Fenster ist vier
 * Wochen ab Montag, und der Server bestimmt es"): `?ab=` geht unverändert an
 * den Server, der normalisiert auf den Montag der ISO-Woche — auch ein
 * Datum, das keiner ist. `kal-prev`/`kal-next` verschieben nur den zuletzt
 * vom Server gelieferten Fensteranfang um volle Wochen (`window.ts`), `kal-
 * today` löscht `ab` ganz — „ohne Parameter die laufende Woche".
 *
 * Schmale Ansicht (< 768px, AK-462): `kal-agenda` statt `kal-grid` — dieselbe
 * Umschaltung wie `Sidebar` in `router.tsx` (`useIsDesktopViewport`), nicht
 * per CSS: Zwei nur optisch versteckte Kopien trügen sonst denselben
 * `data-testid` doppelt im Dokument (`lib/viewport.ts`).
 *
 * **Anlegen** läuft über **ein** Menü (`kal-add-menu`, Spec 45 Regel 10):
 * „Termin" (`duedate-add`, `DueDateSheet` mit `duedate-subject-picker`) und
 * „Aufgabe" (`task-add`, `TaskSheet` mit `task-subject-picker`). Dasselbe
 * Menü hängt am Plus der Tageszelle (`kal-tag-add-<datum>`, R6). Vorbelegt
 * wird „Fällig am" aus `range.today` bzw. dem Tag der Zelle — nie aus einer
 * Client-Uhr (Regel 4). In den Listenansichten ist `range.today` nicht
 * geladen; das Feld bleibt dann leer und `required` (benannte Einschränkung,
 * Spec 45 „Kalender Web").
 */

import { useNavigate, useSearch } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { useCalendar } from '@/features/calendar/api'
import type { CalendarEntry, CalendarResponse } from '@/features/calendar/api'
import { CalendarAddMenu } from '@/features/calendar/CalendarAddMenu'
import { CalendarAgenda } from '@/features/calendar/CalendarAgenda'
import type { CalendarEntryActions } from '@/features/calendar/CalendarEntryRow'
import { CalendarGrid } from '@/features/calendar/CalendarGrid'
import { CalendarSkeleton } from '@/features/calendar/CalendarSkeleton'
import { shiftWindow } from '@/features/calendar/window'
import { DueDateSheet } from '@/features/schedule/DueDateSheet'
import { useTicket, useTicketsList, useTicketStatuses, useUpdateTicket } from '@/features/tasks/api'
import { TaskList } from '@/features/tasks/TaskList'
import { TaskSheet } from '@/features/tasks/TaskSheet'
import { ApiError } from '@/lib/api'
import { ticketsQueryForView } from '@/lib/kalender-view'
import type { KalenderView } from '@/lib/kalender-view'
import { useIsDesktopViewport } from '@/lib/viewport'

const WEEKS_PER_WINDOW = 4

/** Die Wörter der beiden Listenansichten (Bestand Spec 06a). */
const VIEW_EMPTY: Record<KalenderView, { readonly title: string; readonly hint: string }> = {
  ueberfaellig: { title: 'Nichts überfällig.', hint: 'Aufgaben mit abgelaufener Fälligkeit erscheinen hier.' },
  meine: { title: 'Dir ist nichts zugewiesen.', hint: 'Aufgaben erscheinen hier, sobald jemand dir eine gibt.' },
}

function isEmptyWindow(data: CalendarResponse): boolean {
  return data.weeks.every((week) => week.days.every((day) => day.total === 0))
}

/** `true`, solange irgendein Tag der Antwort `is_today` trägt — der Server
 * kennt „heute" bereits (Regel 4), dieser Client vergleicht kein Datum, um
 * zu wissen, ob `kal-today` etwas zu tun hätte. */
function showsCurrentWeek(data: CalendarResponse): boolean {
  return data.weeks.some((week) => week.days.some((day) => day.is_today))
}

/** Ein offenes Anlege-Sheet; `dueOn` fehlt, wo kein Tag feststeht (Listenansicht). */
interface CreateSheet {
  readonly dueOn: string | undefined
}

export function Kalender() {
  const navigate = useNavigate({ from: '/' })
  const search = useSearch({ from: '/' })
  const isDesktop = useIsDesktopViewport()
  const view = search.view
  const isWeeks = view === undefined
  const query = useCalendar(search.ab, { enabled: isWeeks })
  const listQuery = useTicketsList(ticketsQueryForView(view ?? 'meine'), { enabled: !isWeeks })

  const statuses = useTicketStatuses()
  const doneLabel = statuses.data?.find((status) => status.value === 'done')?.label
  const updateMutation = useUpdateTicket()

  const [expandedDates, setExpandedDates] = useState<ReadonlySet<string>>(new Set())
  const [appointmentSheet, setAppointmentSheet] = useState<CreateSheet | null>(null)
  const [taskCreateSheet, setTaskCreateSheet] = useState<CreateSheet | null>(null)
  const [editTicketId, setEditTicketId] = useState<string | null>(null)
  const ticketQuery = useTicket(editTicketId)
  const [doneError, setDoneError] = useState<string | null>(null)

  // Ein Blättern beginnt an einer neuen Woche — an derselben Position stünde
  // sonst wieder „aufgeklappt", obwohl dort ein anderer Tag liegt. Während
  // des Renderns verglichen statt in einem Effekt (dieselbe Bauform wie
  // `DueDateSheet`s `wasOpen`) — ein `setState` synchron im Effektkörper löst
  // eine zweite, vermeidbare Renderrunde aus.
  const [lastAb, setLastAb] = useState(search.ab)
  if (search.ab !== lastAb) {
    setLastAb(search.ab)
    setExpandedDates(new Set())
  }

  function toggleExpand(date: string) {
    setExpandedDates((current) => {
      const next = new Set(current)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })
  }

  function goToAb(ab: string | undefined) {
    void navigate({ search: ab === undefined ? {} : { ab } })
  }

  // R16: Das Web erledigt sofort per Ring, ohne Countdown. `version` kommt mit
  // dem Eintrag (R10) — kein Vorabruf. Ein Fehler (409 …) bleibt nicht stumm.
  const actions: CalendarEntryActions = {
    doneLabel,
    onDone: (entry: CalendarEntry) => {
      if (entry.source_id === null || entry.source_id === undefined || entry.version === null || entry.version === undefined) {
        return
      }
      setDoneError(null)
      updateMutation.mutate(
        { ticketId: entry.source_id, ifMatch: String(entry.version), body: { status: 'done' } },
        {
          onError: (error) => {
            setDoneError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
          },
        },
      )
    },
    // Ein zweiter Klick auf dieselbe Aufgabe nach einem Ladefehler lädt neu.
    onOpenTask: (ticketId: string) => {
      setDoneError(null)
      if (ticketQuery.isError && editTicketId === ticketId) void ticketQuery.refetch()
      else setEditTicketId(ticketId)
    },
  }
  const taskError =
    doneError ?? (ticketQuery.isError ? 'Die Aufgabe konnte nicht geladen werden. Bitte erneut anklicken.' : null)

  const today = query.data?.range.today
  const canNavigate = query.data !== undefined
  const isOnCurrentWeek = query.data !== undefined && showsCurrentWeek(query.data)

  return (
    <section aria-labelledby="kal-heading" data-testid="scope-kalender" className="flex h-full flex-col">
      <header
        data-testid="kal-kopf"
        className="border-border flex min-h-(--row-h) items-center justify-between gap-3 border-b px-(--page-x) py-1"
      >
        <h1 id="kal-heading" className="text-page font-(--font-weight-page)">
          Kalender
        </h1>

        <div className="flex items-center gap-1">
          {isWeeks && (
            <>
              <button
                type="button"
                data-testid="kal-prev"
                aria-label="Vier Wochen zurück"
                disabled={!canNavigate}
                onClick={() => {
                  if (query.data) goToAb(shiftWindow(query.data.range.from, -WEEKS_PER_WINDOW))
                }}
                className="text-muted hover:text-text flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center disabled:opacity-40"
              >
                <ChevronLeft aria-hidden className="size-4" />
              </button>
              <button
                type="button"
                data-testid="kal-next"
                aria-label="Vier Wochen vor"
                disabled={!canNavigate}
                onClick={() => {
                  if (query.data) goToAb(shiftWindow(query.data.range.from, WEEKS_PER_WINDOW))
                }}
                className="text-muted hover:text-text flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center disabled:opacity-40"
              >
                <ChevronRight aria-hidden className="size-4" />
              </button>
              {canNavigate && !isOnCurrentWeek && (
                <button
                  type="button"
                  data-testid="kal-today"
                  onClick={() => {
                    goToAb(undefined)
                  }}
                  className="border-border text-text hover:bg-bg rounded-card text-body min-h-(--tap-min) border px-3 transition-colors duration-(--duration-fast) ease-(--ease-out)"
                >
                  Heute
                </button>
              )}
            </>
          )}

          {/* Die eine primäre Aktion dieses Bereichs — aktives Verb, kein „+
           * Neu" (`design-system`, „Sprache"). `range.today` statt einer
           * Client-Uhr (Regel 4, W-D3): der Server kennt „heute" bereits,
           * derselben Antwort, aus der auch `kal-today` liest. */}
          <CalendarAddMenu
            variant="header"
            triggerTestId="kal-add-menu"
            menuTestId="kal-add-menu-popup"
            triggerLabel="Anlegen"
            onAddAppointment={() => {
              setAppointmentSheet({ dueOn: today })
            }}
            onAddTask={() => {
              setTaskCreateSheet({ dueOn: today })
            }}
          />
        </div>
      </header>

      {taskError !== null && (
        <p data-testid="task-error" role="alert" className="text-label text-status-overdue px-(--page-x) py-2">
          {taskError}
        </p>
      )}

      {!isWeeks && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <TaskList
            query={listQuery}
            subjectId=""
            subjectLabel=""
            canAdd={false}
            emptyTitle={VIEW_EMPTY[view].title}
            emptyHint={VIEW_EMPTY[view].hint}
          />
        </div>
      )}

      {isWeeks && query.isPending && <CalendarSkeleton />}

      {isWeeks && query.isError && (
        <ErrorState
          testId="kal-error"
          retryTestId="kal-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {isWeeks && query.isSuccess && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isDesktop ? (
            <CalendarGrid
              weeks={query.data.weeks}
              expandedDates={expandedDates}
              actions={actions}
              onToggleExpand={toggleExpand}
              onAddAppointment={(date) => {
                setAppointmentSheet({ dueOn: date })
              }}
              onAddTask={(date) => {
                setTaskCreateSheet({ dueOn: date })
              }}
            />
          ) : (
            <CalendarAgenda weeks={query.data.weeks} actions={actions} />
          )}

          {/* Das Raster/die Liste bleibt sichtbar — die Meldung steht
           * darunter, sie ersetzt nichts (Spec, „Drei Zustände"). Eigene
           * `action` statt eines Verweises auf das Menü im Kopf: AK-459
           * verlangt „Text und der Aufforderung" an `kal-empty` selbst.
           * `kal-empty-add`, nicht `duedate-add` — der Bezeichner gehört dem
           * Menüeintrag (K-D1, gleicher Bezeichner wie iOS). */}
          {isEmptyWindow(query.data) && (
            <EmptyState
              testId="kal-empty"
              title="In den nächsten vier Wochen steht nichts an."
              hint="Termine, Aufgaben und Mietfälligkeiten erscheinen hier automatisch."
              action={
                <button
                  type="button"
                  data-testid="kal-empty-add"
                  onClick={() => {
                    setAppointmentSheet({ dueOn: query.data.range.today })
                  }}
                  className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
                >
                  Termin anlegen
                </button>
              }
            />
          )}
        </div>
      )}

      <DueDateSheet
        open={appointmentSheet !== null}
        onOpenChange={(open) => {
          if (!open) setAppointmentSheet(null)
        }}
        dueDate={null}
        {...(appointmentSheet?.dueOn !== undefined ? { initialDueOn: appointmentSheet.dueOn } : {})}
      />

      {/* Anlegen: ohne festes Objekt (`task-subject-picker`, R12). Ändern: das
       * Sheet öffnet erst, wenn `GET /tickets/{id}` geantwortet hat — es
       * braucht die ganze Zeile samt `version` für `If-Match`. */}
      <TaskSheet
        open={taskCreateSheet !== null || (editTicketId !== null && ticketQuery.isSuccess)}
        onOpenChange={(open) => {
          if (open) return
          setTaskCreateSheet(null)
          setEditTicketId(null)
        }}
        subjectId=""
        subjectLabel=""
        ticket={editTicketId !== null && ticketQuery.isSuccess ? ticketQuery.data : null}
        {...(taskCreateSheet?.dueOn !== undefined ? { initialDueOn: taskCreateSheet.dueOn } : {})}
      />
    </section>
  )
}
